const REVEAL_CLASS_MAP = {
  up:         'animate-reveal-up',
  'up-short': 'animate-reveal-up-short',
  down:       'animate-reveal-down',
  'down-short': 'animate-reveal-down-short',
  left:       'animate-reveal-left',
  right:      'animate-reveal-right',
  fade:       'animate-reveal-fade',
  tile:       'animate-reveal-tile',
};

const mobileQuery = window.matchMedia('(max-width: 767px)');

function initScrollReveal() {
  const elements = document.querySelectorAll('[data-reveal]');
  if (!elements.length) return;

  const animating = new Set();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const direction = el.dataset.reveal;
        const once = el.dataset.revealOnce === 'true';

        if (!entry.isIntersecting) {
          // Don't reset while an animation is still running
          if (animating.has(el)) return;

          // Cancel any pending delayed reveal
          if (el._revealTimer) {
            clearTimeout(el._revealTimer);
            el._revealTimer = null;
          }

          // Reset so it can re-animate on next entry
          if (direction === 'letters') {
            el.querySelectorAll('span').forEach(span => {
              span.classList.remove('animate-reveal-letter');
              span.style.opacity = '0';
            });
          } else {
            const animClass = REVEAL_CLASS_MAP[direction];
            if (animClass) el.classList.remove(animClass);
            el.style.opacity = '0';
          }
          return;
        }

        // Letter-by-letter reveal
        if (direction === 'letters') {
          const stagger = parseInt(el.dataset.revealStagger, 10) || 40;
          const spans = el.querySelectorAll('span');
          animating.add(el);
          spans.forEach((span, i) => {
            setTimeout(() => {
              span.style.opacity = '';
              span.classList.add('animate-reveal-letter');
              if (i === spans.length - 1) {
                span.addEventListener('animationend', () => {
                  animating.delete(el);
                  const r = el.getBoundingClientRect();
                  if (r.bottom < 0 || r.top > window.innerHeight) {
                    el.querySelectorAll('span').forEach(s => {
                      s.classList.remove('animate-reveal-letter');
                      s.style.opacity = '0';
                    });
                  }
                }, { once: true });
              }
            }, i * stagger);
          });
          if (once) observer.unobserve(el);
          return;
        }

        // Directional reveal
        const animClass = REVEAL_CLASS_MAP[direction];
        const delay = parseInt(el.dataset.revealDelay, 10) || 0;

        if (!animClass) return;

        const apply = () => {
          el._revealTimer = null;
          animating.add(el);
          el.style.opacity = '';
          el.classList.add(animClass);
          el.addEventListener('animationend', () => {
            animating.delete(el);
            // If element scrolled out before animation finished, reset it now
            const r = el.getBoundingClientRect();
            if (r.bottom < 0 || r.top > window.innerHeight) {
              el.classList.remove(animClass);
              el.style.opacity = '0';
            }
          }, { once: true });
        };

        if (delay > 0) {
          el._revealTimer = setTimeout(apply, delay);
        } else {
          apply();
        }

        if (once) observer.unobserve(el);
      });
    },
    { rootMargin: '0px 0px -150px 0px', threshold: 0.1 }
  );

  elements.forEach((el) => {
    // data-reveal-no-mobile: skip animation entirely on mobile
    if ('revealNoMobile' in el.dataset && mobileQuery.matches) return;

    if (el.dataset.reveal === 'letters') {
      el.querySelectorAll('span').forEach(span => span.style.opacity = '0');
    } else {
      el.style.opacity = '0';
    }
    observer.observe(el);
  });
}

// Scan image wipe — fires after the reveal animation on the same element ends
function initScanAnimate() {
  const els = document.querySelectorAll('[data-scan-animate]');
  if (!els.length) return;

  els.forEach((el) => {
    // Re-trigger each time the reveal animation completes (not child scan animations)
    el.addEventListener('animationend', (e) => {
      if (e.target === el && e.animationName === 'revealFromLeft') {
        el.classList.add('is-visible');
      }
    });

    // Remove is-visible when out of view so the scan wipe replays on re-entry
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          el.classList.remove('is-visible');
        }
      });
    }, { rootMargin: '0px 0px -150px 0px', threshold: 0.1 });

    observer.observe(el);
  });
}

function initCarouselReveal() {
  const carousels = document.querySelectorAll('[data-reveal-carousel]');
  if (!carousels.length) return;

  carousels.forEach((wrapper) => {
    let isAnimating = false;
    let slideTimers = [];

    const getSlides = () => [...wrapper.querySelectorAll('swiper-slide:not(.swiper-slide-duplicate) > *:first-child')];
    getSlides().forEach((s) => s.style.opacity = '0');

    const cancelTimers = () => {
      slideTimers.forEach(clearTimeout);
      slideTimers = [];
    };

    const resetSlides = () => {
      getSlides().forEach((s) => {
        s.classList.remove('animate-reveal-up-short');
        s.style.opacity = '0';
      });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (isAnimating) return;

          const slides = getSlides().filter((s) => {
            const r = s.getBoundingClientRect();
            return r.right > 0 && r.left < window.innerWidth;
          });
          slides.sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);

          if (!slides.length) return;

          isAnimating = true;
          slides.forEach((slide, i) => {
            const t = setTimeout(() => {
              slide.style.opacity = '';
              slide.classList.add('animate-reveal-up-short');
              if (i === slides.length - 1) {
                slide.addEventListener('animationend', () => {
                  getSlides().forEach((s) => s.style.opacity = '');
                  isAnimating = false;
                  // If scrolled out before animation finished, reset immediately
                  const r = wrapper.getBoundingClientRect();
                  if (r.bottom < 0 || r.top > window.innerHeight) {
                    resetSlides();
                  }
                }, { once: true });
              }
            }, i * 100);
            slideTimers.push(t);
          });
        } else {
          // Cancel pending timers before checking isAnimating
          cancelTimers();
          if (isAnimating) {
            isAnimating = false;
          }
          resetSlides();
        }
      });
    }, { rootMargin: '0px 0px -100px 0px', threshold: 0.05 });

    observer.observe(wrapper);
  });
}

function initGraphTiles() {
  const CIRC = 2 * Math.PI * 30;

  function animateTile(tile) {
    tile.querySelectorAll('.graph-ring').forEach((ring, i) => {
      const pct = parseFloat(ring.dataset.percent) || 0;
      const target = CIRC * (1 - pct / 100);
      ring.style.transition = 'none';
      ring.style.strokeDashoffset = CIRC;
      setTimeout(() => {
        ring.style.transition = 'stroke-dashoffset 1s cubic-bezier(0.16, 1, 0.3, 1)';
        ring.style.strokeDashoffset = target;
      }, i * 250 + 50);
    });
  }

  function resetTile(tile) {
    tile.querySelectorAll('.graph-ring').forEach((ring) => {
      ring.style.transition = 'none';
      ring.style.strokeDashoffset = CIRC;
    });
  }

  const tiles = document.querySelectorAll('[data-graph-tile]');
  if (!tiles.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setTimeout(() => animateTile(entry.target), 700);
      } else {
        resetTile(entry.target);
      }
    });
  }, { threshold: 0.3, rootMargin: '0px 0px -100px 0px' });

  tiles.forEach((el) => observer.observe(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal();
    initScanAnimate();
    initGraphTiles();
    initCarouselReveal();
  });
} else {
  initScrollReveal();
  initScanAnimate();
  initGraphTiles();
  initCarouselReveal();
}
